import { useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Center,
  CopyButton,
  Drawer,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconCopy, IconCheck } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { translationApi } from "@services/translationApi";
import { languageLabel } from "@config/languages";

interface Props {
  translationId: string;
  targetLang: string | null;
  onClose: () => void;
}

/**
 * Render a per-model translation value as display text.
 *
 * The backend's `all_texts` entries may be either a plain string (legacy) or an
 * object carrying the translation plus token/price metadata, e.g.
 * `{ text, input_tokens, output_tokens, total_price, ... }`. Show the human
 * translation (`text`) in both cases; only fall back to a JSON dump for
 * genuinely unexpected shapes.
 */
const asText = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && "text" in value) {
    const inner = (value as { text?: unknown }).text;
    if (typeof inner === "string") return inner;
  }
  return JSON.stringify(value, null, 2);
};

export const AllModelsDrawer: React.FC<Props> = ({ translationId, targetLang, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [models, setModels] = useState<Record<string, unknown> | null>(null);
  const [best, setBest] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!targetLang) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setModels(null);
    translationApi
      .getTranslation(translationId, { targetLang, verbose: true })
      .then((res) => {
        if (cancelled) return;
        const entry = res.translations?.[targetLang];
        setModels((entry?.all_texts as Record<string, unknown>) ?? {});
        setBest(entry?.best_models ?? null);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [translationId, targetLang]);

  const entries = models ? Object.entries(models) : [];

  return (
    <Drawer
      opened={targetLang != null}
      onClose={onClose}
      position="right"
      size="lg"
      title={
        <Group gap="xs">
          <Text fw={600}>{t("translation.allModels")}</Text>
          {targetLang && <Badge variant="light">{languageLabel(targetLang)}</Badge>}
        </Group>
      }
    >
      {loading && (
        <Center py="xl">
          <Loader />
        </Center>
      )}

      {error && <Alert color="red">{error}</Alert>}

      {!loading && !error && (
        <Stack gap="md">
          {best && (
            <Text size="sm" c="dimmed">
              {t("translation.bestModel")}: <b>{best}</b>
            </Text>
          )}
          {entries.length === 0 ? (
            <Text c="dimmed" size="sm">
              {t("translation.noModelData")}
            </Text>
          ) : (
            entries.map(([model, value]) => {
              const content = asText(value);
              return (
                <Paper key={model} withBorder p="md" radius="sm">
                  <Group justify="space-between" mb={6}>
                    <Badge
                      variant={model === best ? "filled" : "outline"}
                      color={model === best ? "brand" : "gray"}
                    >
                      {model}
                    </Badge>
                    <CopyButton value={content} timeout={1500}>
                      {({ copied, copy }) => (
                        <Tooltip label={copied ? t("translation.copied") : t("translation.copy")}>
                          <ActionIcon
                            variant="subtle"
                            color={copied ? "green" : "gray"}
                            size="sm"
                            onClick={copy}
                            aria-label={copied ? t("translation.copied") : t("translation.copy")}
                          >
                            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Group>
                  <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                    {content}
                  </Text>
                </Paper>
              );
            })
          )}
        </Stack>
      )}
    </Drawer>
  );
};
